import { Icon, IconName } from '@/components/Icon'

export type TagAppearance = 'solid' | 'stroke' | 'subtle'
export type TagColor =
	| 'brand'
	| 'content1'
	| 'content2'
	| 'content3'
	| 'success'
	| 'warning'
	| 'danger'
	| 'note'
	| 'overlay'
export type TagSize = 'xs' | 'sm' | 'md'

interface TagProps {
	label: string
	appearance?: TagAppearance
	color?: TagColor
	size?: TagSize
	iconLeft?: IconName
	iconRight?: IconName
	spacesClass?: string
	className?: string
}

const iconSizeMap: Record<TagSize, number> = {
	xs: 12,
	sm: 16,
	md: 16
}

const sizeClasses: Record<TagSize, string> = {
	xs: 'spaces:text-12/16 spaces:gap-4 spaces:px-3 spaces:rounded-4',
	sm: 'spaces:text-14/16 spaces:px-7 spaces:py-3 spaces:gap-4 spaces:rounded-8',
	md: 'spaces:text-16/24 spaces:px-11 spaces:py-3 spaces:gap-7 spaces:rounded-12'
}

function getColorClasses (appearance: TagAppearance, color: TagColor): string {
	if (appearance === 'solid') {
		switch (color) {
			case 'brand':
				return 'spaces:bg-brand-base spaces:text-brand-on-base spaces:border-transparent'
			case 'content1':
				return 'spaces:bg-canvas-inverse spaces:text-content-inverse spaces:border-transparent'
			case 'content2':
				return 'spaces:bg-canvas-inverse spaces:text-content-inverse spaces:border-transparent'
			case 'content3':
				return 'spaces:bg-canvas-inverse spaces:text-content-inverse spaces:border-transparent'
			case 'success':
				return 'spaces:bg-success-base spaces:text-success-on-base spaces:border-transparent'
			case 'warning':
				return 'spaces:bg-warning-base spaces:text-warning-on-base spaces:border-transparent'
			case 'danger':
				return 'spaces:bg-danger-base spaces:text-danger-on-base spaces:border-transparent'
			case 'note':
				return 'spaces:bg-note-base spaces:text-note-on-base spaces:border-transparent'
			case 'overlay':
				return 'spaces:bg-white/90 spaces:text-black/75 spaces:backdrop-blur-default spaces:border-transparent'
		}
	}
	if (appearance === 'subtle') {
		switch (color) {
			case 'brand':
				return 'spaces:bg-brand-subtle spaces:text-brand-on-subtle spaces:border-transparent'
			case 'content1':
				return 'spaces:bg-canvas-4 spaces:text-content-1 spaces:border-transparent'
			case 'content2':
				return 'spaces:bg-canvas-4 spaces:text-content-2 spaces:border-transparent'
			case 'content3':
				return 'spaces:bg-canvas-4 spaces:text-content-3 spaces:border-transparent'
			case 'success':
				return 'spaces:bg-success-subtle spaces:text-success-on-subtle spaces:border-transparent'
			case 'warning':
				return 'spaces:bg-warning-subtle spaces:text-warning-on-subtle spaces:border-transparent'
			case 'danger':
				return 'spaces:bg-danger-subtle spaces:text-danger-on-subtle spaces:border-transparent'
			case 'note':
				return 'spaces:bg-note-subtle spaces:text-note-on-subtle spaces:border-transparent'
			case 'overlay':
				return 'spaces:bg-black/55 spaces:text-white spaces:backdrop-blur-default spaces:border-transparent'
		}
	}
	// stroke
	switch (color) {
		case 'brand':
			return 'spaces:bg-canvas-base spaces:text-brand-content spaces:border-brand-base'
		case 'content1':
			return 'spaces:bg-canvas-base spaces:text-content-1 spaces:border-canvas-3'
		case 'content2':
			return 'spaces:bg-canvas-base spaces:text-content-2 spaces:border-canvas-3'
		case 'content3':
			return 'spaces:bg-canvas-base spaces:text-content-3 spaces:border-canvas-3'
		case 'success':
			return 'spaces:bg-canvas-base spaces:text-success-content spaces:border-success-base'
		case 'warning':
			return 'spaces:bg-canvas-base spaces:text-warning-content spaces:border-warning-base'
		case 'danger':
			return 'spaces:bg-canvas-base spaces:text-danger-content spaces:border-danger-base'
		case 'note':
			return 'spaces:bg-canvas-base spaces:text-note-content spaces:border-note-base'
		case 'overlay':
			return 'spaces:bg-black/55 spaces:text-white spaces:backdrop-blur-default spaces:border-white/70'
	}
}

export function Tag ({
	label,
	appearance = 'stroke',
	color = 'content2',
	size = 'sm',
	iconLeft,
	iconRight,
	spacesClass = '',
	className = ''
}: TagProps) {
	const iconPx = iconSizeMap[size]

	return (
		<div
			className={`spaces-tag spaces:inline-flex spaces:items-center spaces:font-default spaces:border spaces:whitespace-nowrap ${
				sizeClasses[size]
			} ${getColorClasses(appearance, color)} ${spacesClass} ${className}`}
		>
			{iconLeft && <Icon name={iconLeft} size={iconPx} />}
			<span>{label}</span>
			{iconRight && <Icon name={iconRight} size={iconPx} />}
		</div>
	)
}
